import React, { useState } from 'react';
import type { Api } from '../api';
import type { UserDto } from '../../../../shared/types';
import { Icon } from '../icons';
import { Avatar, ErrorBanner, PasswordInput, Pill, Row } from '../ui';

/** Riga utente (commerciale o referente di un cliente). */
export function UserRow({
  user,
  selected,
  onSelect,
  right,
}: {
  user: UserDto;
  selected?: boolean;
  onSelect?: () => void;
  right?: React.ReactNode;
}) {
  return (
    <Row onClick={onSelect} active={selected}>
      <Avatar name={user.name} />
      <div className="grow">
        <div className="title">{user.name}</div>
        <div className="sub">{user.email}</div>
      </div>
      {!user.active && <Pill tone="bad">disattivato</Pill>}
      {user.seeAll && <Pill tone="good" title="Vede e modifica tutto, come te">socio</Pill>}
      {right}
      {onSelect && <Icon name="chevronRight" size={16} className="muted" />}
    </Row>
  );
}

/** Modulo di creazione accesso (nome, email, password). */
export function UserForm({
  api,
  role,
  clientId,
  onAdded,
  label = 'Aggiungi accesso',
}: {
  api: Api;
  role: 'agent' | 'client';
  clientId?: number;
  onAdded: () => Promise<void> | void;
  label?: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/admin/users', { role, name, email, password: password || undefined, clientId: role === 'client' ? clientId : null });
      setName('');
      setEmail('');
      setPassword('');
      await onAdded();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={add} className="card stack" style={{ gap: 8 }}>
      <div className="eyebrow">{label}</div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="hstack wrap">
        <input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} required style={{ flex: 1, minWidth: 120 }} />
        <input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ flex: 1.3, minWidth: 160 }} />
      </div>
      <div className="hstack wrap">
        <PasswordInput
          placeholder="Password (vuota: entra solo con Google)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          style={{ flex: 1, minWidth: 160 }}
        />
        <button className="btn dark" type="submit" disabled={busy} title="Crea l’accesso al CRM per questa persona">
          <Icon name="plus" size={16} /> Aggiungi
        </button>
      </div>
    </form>
  );
}

/** Azioni su un accesso: nuova password e attiva/disattiva. */
export function UserActions({ api, user, onChanged }: { api: Api; user: UserDto; onChanged: () => Promise<void> | void }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => wrap(() => api.put(`/api/admin/users/${user.id}`, { active: !user.active }));
  const toggleAll = () => wrap(() => api.put(`/api/admin/users/${user.id}`, { seeAll: !user.seeAll }));
  const reset = (e: React.FormEvent) => {
    e.preventDefault();
    wrap(async () => {
      await api.put(`/api/admin/users/${user.id}`, { password: newPassword });
      setResetOpen(false);
      setNewPassword('');
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    });
  };

  return (
    <div className="stack" style={{ gap: 8 }}>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="hstack wrap">
        <button type="button" className="btn small" disabled={busy} title="Scegli una password nuova per questa persona" onClick={() => setResetOpen((o) => !o)}>
          <Icon name="key" size={15} /> {done ? 'Password aggiornata' : 'Nuova password'}
        </button>
        <button
          type="button"
          className={`btn small${user.active ? '' : ' good'}`}
          disabled={busy}
          title={user.active ? 'Non potrà più entrare: i suoi dati restano' : 'Potrà entrare di nuovo'}
          onClick={toggle}
        >
          {user.active ? 'Disattiva accesso' : 'Riattiva accesso'}
        </button>
        {user.role === 'agent' && (
          <button
            type="button"
            className={`btn small${user.seeAll ? ' good' : ''}`}
            disabled={busy}
            onClick={toggleAll}
            title="Il socio vede e modifica tutte le trattative, i preventivi, le fatture e i loro documenti, come te."
          >
            <Icon name="users" size={15} /> {user.seeAll ? 'Socio: vede tutto' : 'Fai vedere tutto (socio)'}
          </button>
        )}
      </div>
      {resetOpen && (
        <form className="hstack" onSubmit={reset}>
          <PasswordInput
            placeholder="Nuova password (minimo 8 caratteri)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={8}
            required
            autoFocus
          />
          <button className="btn small dark" type="submit" disabled={busy}>
            Salva
          </button>
        </form>
      )}
    </div>
  );
}

/** Lista accessi con azioni inline + modulo di aggiunta (usato per i referenti di un cliente). */
export function UsersManager({
  api,
  role,
  clientId,
  users,
  onChanged,
}: {
  api: Api;
  role: 'agent' | 'client';
  clientId?: number;
  users: UserDto[];
  onChanged: () => Promise<void> | void;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  return (
    <div className="stack" style={{ gap: 8 }}>
      {users.length === 0 && <div className="muted small">Nessun accesso creato.</div>}
      {users.map((u) => (
        <div key={u.id}>
          <UserRow user={u} selected={openId === u.id} onSelect={() => setOpenId(openId === u.id ? null : u.id)} />
          {openId === u.id && (
            <div className="row-detail">
              <UserActions api={api} user={u} onChanged={onChanged} />
            </div>
          )}
        </div>
      ))}
      <UserForm api={api} role={role} clientId={clientId} onAdded={onChanged} label={role === 'client' ? 'Nuovo referente' : 'Nuovo commerciale'} />
    </div>
  );
}
